import {fetchMarketingOptOutStatus, submitMarketingOptOut} from "marketing/marketingApi";
import {fetchOptOutStatus, submitOptOut, type OptOutStatus} from "./unsubscribeApi";

/**
 * Which mail an unsubscribe link stops (kitecorp/transferify#163).
 *
 * The two are deliberately different endpoints rather than one with a flag: `transfers` writes the
 * RECIPIENT suppression list — a global block that stops every sender reaching that address — while
 * `marketing` writes an ACCOUNT preference and leaves transfer and billing mail working. One URL serving
 * both would be one conditional away from a marketing unsubscribe killing somebody's delivery mail.
 *
 * <p><strong>They also answer different SHAPES</strong> — `{email, unsubscribed}` and `{email, optedOut}`
 * — which is why each scope brings its own reader rather than sharing one. Pointing the recipient reader
 * at the marketing endpoint made `unsubscribed` permanently undefined, so an account that had already
 * stopped offers was shown the confirm step again and the "already stopped" copy could never render.
 *
 * React-free so the mapping is assertable without rendering anything.
 */
export type UnsubscribeScope = "transfers" | "marketing";

export type ScopeClient = {
	/** Resolves what the token refers to WITHOUT recording anything. */
	status: (token: string) => Promise<OptOutStatus>;
	/** Records the opt-out. */
	optOut: (token: string) => Promise<OptOutStatus>;
};

const CLIENTS: Record<UnsubscribeScope, ScopeClient> = {
	transfers: {status: fetchOptOutStatus, optOut: submitOptOut},
	marketing: {
		// Normalised into the page's own shape at the seam. `email` is nullable in the marketing schema,
		// and passing null through would render the literal "null" inside the confirmation copy.
		status: async (token) => {
			const {email, optedOut} = await fetchMarketingOptOutStatus(token);
			return {email: email ?? "", unsubscribed: optedOut};
		},
		optOut: async (token) => {
			const {email, optedOut} = await submitMarketingOptOut(token);
			return {email: email ?? "", unsubscribed: optedOut};
		},
	},
};

/** @returns the reader/writer pair for this scope. */
export function clientFor(scope: UnsubscribeScope): ScopeClient {
	return CLIENTS[scope];
}
